/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * ShareProgressModal - Modal for sharing SAT progress with canvas-based share card
 *
 * Generates a shareable image showing scores, trends, badges and levels.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { X, Download, Copy, Share2, Check, Loader2, RefreshCw } from 'lucide-react'
import { useShareCardData } from '../../hooks/usePerSubjectScores'
import { loadImage, loadBadgeImages } from '../../utils/imageLoading'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ShareProgressModal')

/**
 * Draw a rounded rectangle on canvas
 */
const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 * ShareProgressModal - Modal for generating and sharing progress card
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close callback
 * @param {string} props.username - User's display name
 */
const ShareProgressModal = ({ isOpen, onClose, username = '' }) => {
  const canvasRef = useRef(null)
  const isMountedRef = useRef(true)
  const copyTimeoutRef = useRef(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [copied, setCopied] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [imageError, setImageError] = useState(null)

  // Track mounted state and cleanup timeout on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const {
    loading,
    error,
    fetch: fetchShareData,
    scores,
    badgeCount,
    highestLevel,
    topBadges,
    streak,
    goal,
    totalChallenges,
    confidenceLevels
  } = useShareCardData({ autoFetch: false })

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchShareData()
    }
  }, [isOpen, fetchShareData])

  // Clean up image URL when modal closes to free memory
  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null)
      setImageError(null)
    }
  }, [isOpen])

  // Generate canvas image when data is ready
  // Note: imageUrl intentionally NOT in dependency array to avoid infinite loop
  const generateImage = useCallback(async () => {
    const canvas = canvasRef.current
    if (!scores || !canvas) return

    // Clear previous image to free memory
    setImageUrl(null)
    setImageError(null)
    setGenerating(true)

    try {
      // Pre-load badge images and logo
      const badgesToLoad = topBadges?.slice(0, 3) || []
      const [badgeImages, logoImage] = await Promise.all([
        loadBadgeImages(badgesToLoad),
        loadImage('/deaveris-icon.svg').catch(() => null)
      ])

      // Check canvas is still available after async operation
      if (!canvasRef.current) {
        logger.warn('Canvas no longer available after image loading')
        return
      }

      const ctx = canvas.getContext('2d')

      // Validate canvas context was created successfully
      if (!ctx) {
        throw new Error('Failed to create canvas context')
      }

      const width = 1080
      const height = 1080

      canvas.width = width
      canvas.height = height

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height)
      gradient.addColorStop(0, '#f8fafc')
      gradient.addColorStop(1, '#e2e8f0')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, height)

      // Header bar with gradient
      const headerHeight = 160
      const headerGradient = ctx.createLinearGradient(0, 0, width, 0)
      headerGradient.addColorStop(0, '#1e3a5f')
      headerGradient.addColorStop(0.5, '#1e40af')
      headerGradient.addColorStop(1, '#3b82f6')
      ctx.fillStyle = headerGradient
      ctx.fillRect(0, 0, width, headerHeight)

      // Decorative accent line
      ctx.fillStyle = '#60a5fa'
      ctx.fillRect(0, headerHeight - 4, width, 4)

      // Logo and brand name
      const logoSize = 50
      const brandText = 'DEAVERIS'
      ctx.font = 'bold 52px system-ui, -apple-system, sans-serif'
      const brandWidth = ctx.measureText(brandText).width
      const totalBrandWidth = logoImage ? logoSize + 15 + brandWidth : brandWidth
      const brandStartX = (width - totalBrandWidth) / 2

      // Draw logo if loaded
      if (logoImage) {
        ctx.drawImage(logoImage, brandStartX, 25, logoSize, logoSize)
      }

      // Brand name - DEAVERIS
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'left'
      ctx.fillText(
        brandText,
        logoImage ? brandStartX + logoSize + 15 : (width - brandWidth) / 2,
        65
      )

      // Subtitle
      ctx.font = '600 28px system-ui, -apple-system, sans-serif'
      ctx.fillStyle = '#93c5fd'
      ctx.textAlign = 'center'
      ctx.fillText('My SAT Journey', width / 2, 105)

      // Username
      if (username) {
        ctx.font = '24px system-ui, -apple-system, sans-serif'
        ctx.fillStyle = '#bfdbfe'
        ctx.fillText(`@${username}`, width / 2, 140)
      }

      // Scores section
      const scoreY = 190
      const scoreBoxWidth = 300
      const scoreBoxHeight = 220
      const scoreGap = 30

      // Math score box with confidence stars
      const mathX = (width - (3 * scoreBoxWidth + 2 * scoreGap)) / 2
      drawScoreBox(
        ctx,
        mathX,
        scoreY,
        scoreBoxWidth,
        scoreBoxHeight,
        'MATH',
        scores.math,
        '#3b82f6',
        false,
        confidenceLevels?.math
      )

      // R&W score box with confidence stars
      const rwX = mathX + scoreBoxWidth + scoreGap
      drawScoreBox(
        ctx,
        rwX,
        scoreY,
        scoreBoxWidth,
        scoreBoxHeight,
        'R&W',
        scores.readingWriting,
        '#10b981',
        false,
        confidenceLevels?.rw
      )

      // Total score box
      const totalX = rwX + scoreBoxWidth + scoreGap
      drawScoreBox(
        ctx,
        totalX,
        scoreY,
        scoreBoxWidth,
        scoreBoxHeight,
        'TOTAL',
        {
          currentScore: scores.combined?.currentScore,
          predictedScore: scores.combined?.predictedScore,
          trend: scores.combined?.trend
        },
        '#6366f1',
        true
      )

      // Goal progress bar (if goal is set)
      let goalBarEndY = scoreY + scoreBoxHeight + 30
      if (goal && scores.combined?.predictedScore) {
        const goalY = scoreY + scoreBoxHeight + 40
        const goalProgress = Math.min(
          100,
          Math.round((scores.combined.predictedScore / goal) * 100)
        )

        // Goal label
        ctx.fillStyle = '#475569'
        ctx.font = '22px system-ui, -apple-system, sans-serif'
        ctx.textAlign = 'left'
        ctx.fillText(`Goal: ${goal}`, 100, goalY)

        ctx.textAlign = 'right'
        ctx.fillText(`${goalProgress}% there`, width - 100, goalY)

        // Progress bar background
        ctx.fillStyle = '#e2e8f0'
        roundRect(ctx, 100, goalY + 15, width - 200, 20, 10)
        ctx.fill()

        // Progress bar fill
        const progressGradient = ctx.createLinearGradient(100, 0, width - 100, 0)
        progressGradient.addColorStop(0, '#3b82f6')
        progressGradient.addColorStop(0.5, '#8b5cf6')
        progressGradient.addColorStop(1, '#ec4899')
        ctx.fillStyle = progressGradient
        roundRect(ctx, 100, goalY + 15, (width - 200) * (goalProgress / 100), 20, 10)
        ctx.fill()

        goalBarEndY = goalY + 55
      }

      // Badges section
      const badgesY = goalBarEndY + 20
      if (topBadges && topBadges.length > 0) {
        // Badges label
        ctx.fillStyle = '#475569'
        ctx.font = 'bold 24px system-ui, -apple-system, sans-serif'
        ctx.textAlign = 'left'
        ctx.fillText('BADGES', 100, badgesY)

        // Badge count
        ctx.font = '20px system-ui, -apple-system, sans-serif'
        ctx.textAlign = 'right'
        ctx.fillText(`${badgeCount} earned`, width - 100, badgesY)

        // Badge circles with rarity colors
        const badgeSize = 50
        const badgeGap = 20
        const badgesStartX = (width - (topBadges.length * (badgeSize + badgeGap) - badgeGap)) / 2

        const rarityColors = {
          LEGENDARY: '#f59e0b',
          EPIC: '#8b5cf6',
          RARE: '#3b82f6',
          UNCOMMON: '#10b981',
          COMMON: '#6b7280'
        }

        topBadges.slice(0, 3).forEach((badge, i) => {
          const bx = badgesStartX + i * (badgeSize + badgeGap)
          const by = badgesY + 15
          const color = rarityColors[badge.rarity] || rarityColors.COMMON
          const loadedImage = badgeImages[i]

          // Badge circle background (rarity color ring)
          ctx.beginPath()
          ctx.arc(bx + badgeSize / 2, by + badgeSize / 2, badgeSize / 2 + 4, 0, Math.PI * 2)
          ctx.fillStyle = color
          ctx.fill()

          // White inner circle
          ctx.beginPath()
          ctx.arc(bx + badgeSize / 2, by + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2)
          ctx.fillStyle = '#ffffff'
          ctx.fill()

          // Draw badge image or fallback star
          if (loadedImage) {
            // Draw the actual badge image
            ctx.save()
            ctx.beginPath()
            ctx.arc(bx + badgeSize / 2, by + badgeSize / 2, badgeSize / 2 - 2, 0, Math.PI * 2)
            ctx.clip()
            ctx.drawImage(loadedImage, bx + 2, by + 2, badgeSize - 4, badgeSize - 4)
            ctx.restore()
          } else {
            // Fallback: Star icon
            ctx.fillStyle = color
            ctx.font = '28px system-ui, -apple-system, sans-serif'
            ctx.textAlign = 'center'
            ctx.fillText('\u2605', bx + badgeSize / 2, by + badgeSize / 2 + 10)
          }
        })

        // "+X more" if there are more badges
        if (badgeCount > 3) {
          const moreX = badgesStartX + topBadges.slice(0, 3).length * (badgeSize + badgeGap)
          ctx.fillStyle = '#94a3b8'
          ctx.font = '20px system-ui, -apple-system, sans-serif'
          ctx.textAlign = 'left'
          ctx.fillText(`+${badgeCount - 3} more`, moreX, badgesY + badgeSize / 2 + 20)
        }
      }

      // Stats bar
      const statsY = badgesY + 90
      ctx.fillStyle = '#f1f5f9'
      roundRect(ctx, 100, statsY, width - 200, 80, 16)
      ctx.fill()

      // Streak
      ctx.fillStyle = '#475569'
      ctx.font = '22px system-ui, -apple-system, sans-serif'
      ctx.textAlign = 'left'
      const streakText =
        streak?.currentStreak > 0
          ? `\uD83D\uDD25 ${streak.currentStreak}-day streak`
          : '\uD83D\uDD25 Start a streak!'
      ctx.fillText(streakText, 150, statsY + 48)

      // Level
      ctx.textAlign = 'center'
      ctx.fillText(`\uD83D\uDCDA Level ${highestLevel}`, width / 2, statsY + 48)

      // Challenges
      ctx.textAlign = 'right'
      ctx.fillText(`\uD83C\uDFAF ${totalChallenges} challenges`, width - 150, statsY + 48)

      // Footer bar with gradient (matching header)
      const footerHeight = 80
      const footerY = height - footerHeight
      const footerGradient = ctx.createLinearGradient(0, 0, width, 0)
      footerGradient.addColorStop(0, '#1e3a5f')
      footerGradient.addColorStop(0.5, '#1e40af')
      footerGradient.addColorStop(1, '#3b82f6')
      ctx.fillStyle = footerGradient
      ctx.fillRect(0, footerY, width, footerHeight)

      // Accent line at top of footer
      ctx.fillStyle = '#60a5fa'
      ctx.fillRect(0, footerY, width, 4)

      // URL
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 26px system-ui, -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(
        username ? `deaveris.com/u/${username}` : 'deaveris.com',
        width / 2,
        footerY + 38
      )

      // Date
      const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
      ctx.fillStyle = '#93c5fd'
      ctx.font = '20px system-ui, -apple-system, sans-serif'
      ctx.fillText(today, width / 2, footerY + 65)

      // Generate image URL
      const dataUrl = canvas.toDataURL('image/png')
      if (isMountedRef.current) {
        setImageUrl(dataUrl)
      }
    } catch (err) {
      logger.error('Image generation failed:', err)

      if (!isMountedRef.current) return

      // Provide specific error message for canvas quota errors
      if (
        err.name === 'QuotaExceededError' ||
        err.message?.includes('quota') ||
        err.message?.includes('size')
      ) {
        setImageError('Image too large for your device. Try again later.')
      } else {
        setImageError('Unable to generate share image. Please try again.')
      }
    } finally {
      if (isMountedRef.current) {
        setGenerating(false)
      }
    }
  }, [
    scores,
    badgeCount,
    highestLevel,
    username,
    topBadges,
    streak,
    goal,
    totalChallenges,
    confidenceLevels
  ])

  // Generate image when scores are loaded
  useEffect(() => {
    if (scores && !loading) {
      generateImage()
    }
  }, [scores, loading, generateImage])

  // Handle download
  const handleDownload = () => {
    if (!imageUrl) return

    const link = document.createElement('a')
    link.download = `sat-progress-${Date.now()}.png`
    link.href = imageUrl
    link.click()
  }

  // Handle copy with proper cleanup and timeout
  const handleCopy = async () => {
    if (!canvasRef.current) return

    try {
      const blob = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Blob generation timeout')), 10000)
        canvasRef.current.toBlob((b) => {
          clearTimeout(timeout)
          resolve(b)
        }, 'image/png')
      })

      if (!isMountedRef.current) return

      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])

      if (!isMountedRef.current) return

      setCopied(true)
      copyTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setCopied(false)
        }
      }, 2000)
    } catch (err) {
      logger.error('Failed to copy:', err)
    }
  }

  // Handle native share with proper cleanup
  const handleShare = async () => {
    if (!canvasRef.current) return

    try {
      const blob = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Blob generation timeout')), 10000)
        canvasRef.current.toBlob((b) => {
          clearTimeout(timeout)
          resolve(b)
        }, 'image/png')
      })

      if (!isMountedRef.current) return

      const file = new File([blob], 'sat-progress.png', { type: 'image/png' })

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My SAT Progress',
          text: 'Check out my SAT practice progress!'
        })
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.error('Failed to share:', err)
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Share Your Progress
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4">
            {/* Canvas must be outside conditional to persist during generation */}
            <canvas ref={canvasRef} className="hidden" />

            {loading || generating ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-3" />
                <p className="text-gray-600 dark:text-gray-400">
                  {loading ? 'Loading your progress...' : 'Generating share card...'}
                </p>
              </div>
            ) : error || imageError ? (
              <div className="text-center py-12">
                <p className="text-red-600 dark:text-red-400 mb-4">{error || imageError}</p>
                <button
                  onClick={() => {
                    setImageError(null)
                    if (error) {
                      fetchShareData()
                    } else {
                      generateImage()
                    }
                  }}
                  className="btn btn-secondary flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {/* Preview */}
                {imageUrl && (
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 mb-4">
                    <img
                      src={imageUrl}
                      alt="Share card preview"
                      className="w-full rounded-lg shadow"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={handleDownload}
                    disabled={!imageUrl}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>

                  <button
                    onClick={handleCopy}
                    disabled={!imageUrl}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>

                  {typeof navigator.canShare === 'function' && (
                    <button
                      onClick={handleShare}
                      disabled={!imageUrl}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Helper function to draw a score box on canvas
 */
function drawScoreBox(
  ctx,
  x,
  y,
  width,
  height,
  label,
  data,
  color,
  isTotal = false,
  confidence = null
) {
  // Box background
  ctx.fillStyle = '#ffffff'
  roundRect(ctx, x, y, width, height, 16)
  ctx.fill()

  // Box border
  ctx.strokeStyle = color
  ctx.lineWidth = 3
  roundRect(ctx, x, y, width, height, 16)
  ctx.stroke()

  // Label
  ctx.fillStyle = color
  ctx.font = 'bold 24px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(label, x + width / 2, y + 40)

  if (!data?.currentScore && !data?.predictedScore) {
    ctx.fillStyle = '#94a3b8'
    ctx.font = '28px system-ui, -apple-system, sans-serif'
    ctx.fillText('--', x + width / 2, y + height / 2 + 10)
    return
  }

  // Current score
  if (data?.currentScore) {
    ctx.fillStyle = '#64748b'
    ctx.font = '16px system-ui, -apple-system, sans-serif'
    ctx.fillText('Current', x + width / 2, y + 68)

    ctx.fillStyle = '#1e293b'
    ctx.font = `bold ${isTotal ? '42px' : '36px'} system-ui, -apple-system, sans-serif`
    ctx.fillText(data.currentScore.toString(), x + width / 2, y + 108)
  }

  // Predicted score
  if (data?.predictedScore) {
    ctx.fillStyle = '#64748b'
    ctx.font = '16px system-ui, -apple-system, sans-serif'
    ctx.fillText('Predicted', x + width / 2, y + 138)

    ctx.fillStyle = color
    ctx.font = `bold ${isTotal ? '32px' : '28px'} system-ui, -apple-system, sans-serif`
    ctx.fillText(data.predictedScore.toString(), x + width / 2, y + 170)
  }

  // Confidence stars (for non-total boxes)
  if (!isTotal && confidence?.stars > 0) {
    const stars = confidence.stars
    const maxStars = 5
    const starSize = 16
    const starGap = 4
    const totalStarsWidth = maxStars * starSize + (maxStars - 1) * starGap
    const startX = x + (width - totalStarsWidth) / 2

    for (let i = 0; i < maxStars; i++) {
      const sx = startX + i * (starSize + starGap)
      const sy = y + height - 35
      ctx.fillStyle = i < stars ? '#fbbf24' : '#e5e7eb'
      ctx.font = `${starSize}px system-ui, -apple-system, sans-serif`
      ctx.textAlign = 'left'
      ctx.fillText('\u2605', sx, sy)
    }
  } else if (data?.trend?.change) {
    // Trend (for total box)
    const change = data.trend.change
    const trendColor = change > 0 ? '#16a34a' : change < 0 ? '#dc2626' : '#64748b'
    const arrow = change > 0 ? '\u2191' : change < 0 ? '\u2193' : ''

    ctx.fillStyle = trendColor
    ctx.font = 'bold 18px system-ui, -apple-system, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(`${arrow}${change > 0 ? '+' : ''}${change}`, x + width / 2, y + height - 20)
  }
}

ShareProgressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  username: PropTypes.string
}

export default ShareProgressModal
